import React, { ReactElement } from 'react';
import classNames from 'classnames';
import Downshift from 'downshift';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { formatMessage } from '@/utils/i18n';

interface SimpleDropdownProps {
  id?: string;
  options: ReactElement[];
  disabled?: boolean;
  onSelect: (item: ReactElement) => void;
  children: ReactElement;
  className?: string;
  'data-testid'?: string;
  visibleCount?: number;
  'aria-labelledby'?: string;
  'aria-label'?: string;
}

const SimpleDropdown: React.FC<SimpleDropdownProps> = ({
  id,
  options,
  disabled,
  onSelect,
  children,
  className,
  'data-testid': dataTestId,
  visibleCount,
  'aria-labelledby': ariaLabelledby,
  'aria-label': ariaLabel,
}) => (
  <Downshift
    onSelect={selectedItem => {
      if (selectedItem) {
        onSelect(selectedItem);
      }
    }}
    getA11yStatusMessage={({ isOpen, highlightedIndex, resultCount, highlightedItem }) => {
      if (!isOpen) {
        return '';
      }
      if (resultCount === 0) {
        return formatMessage('Common.Accessibility.NoOptions');
      }

      if (highlightedIndex !== null && highlightedIndex >= 0 && highlightedItem) {
        const itemText = highlightedItem.props.children || highlightedItem.props.label || '';
        return `${itemText}`;
      }
      return formatMessage('Common.Accessibility.OptionsCount', { count: resultCount });
    }}
  >
    {({ getToggleButtonProps, getMenuProps, getItemProps, isOpen, highlightedIndex, selectHighlightedItem }) => {
      const toggleButtonProps = getToggleButtonProps({ 'aria-label': ariaLabel });
      const handleOnKeyDown = (event: React.KeyboardEvent) => {
        // let Downshift handle the keydown event first otherwise the dropdown won't close when we select
        toggleButtonProps.onKeyDown?.(event);

        if (event.key === ' ') {
          event.preventDefault();
          selectHighlightedItem(); // Select the highlighted item, without this the space key won't select anything
        }
      };

      return (
        <div className={classNames(className, 'dropdown-extended', { disabled })} data-testid={dataTestId} aria-labelledby={ariaLabelledby}>
          <button
            id={id}
            type="button"
            className="dropdown-child-container"
            disabled={disabled}
            {...toggleButtonProps}
            onKeyDown={event => handleOnKeyDown(event)}
            role="combobox"
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-readonly="true"
          >
            <div className="input-div">{children}</div>
          </button>
          {isOpen && !disabled && (
            <div className="dropdown-extended-options" {...getMenuProps()}>
              <ul role="listbox">
                <PerfectScrollbar style={{ '--visible-count': visibleCount } as React.CSSProperties}>
                  {options.map((option, idx) => (
                    <li
                      key={idx}
                      {...getItemProps({
                        item: option,
                        index: idx,
                        className: highlightedIndex === idx ? 'active' : '',
                      })}
                      role="option"
                      aria-selected={highlightedIndex === idx}
                    >
                      {option}
                    </li>
                  ))}
                </PerfectScrollbar>
              </ul>
            </div>
          )}
        </div>
      );
    }}
  </Downshift>
);

export default SimpleDropdown;
